import type { PlaybookOperation } from "@repo/app-types/entities/configurationAgent";
import { PLAYBOOK_CONFIGURATION_AGENT_ID } from "@repo/app-types/entities/configurationAgent";
import type { OutcomeDefinition } from "@repo/app-types/entities/outcome";
import {
  asPlaybookId,
  type PlaybookCriterionNode,
  type PlaybookNodeId,
  type PlaybookPublishTarget,
  type PlaybookSectionNode,
} from "@repo/app-types/entities/playbook";
import type { RecordingAssessmentFeedbackStatus } from "@repo/app-types/entities/recordingAssessmentFeedback";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { applyPlaybookOperation } from "@repo/app-types/helpers/playbookTreeOperations";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { useConfirmationModal } from "@repo/ui/hooks/useConfirmationModal";
import { ConfirmationModal } from "@repo/ui/Modal/confirmationModal";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import type { UIMessage } from "ai";
import { useCallback, useRef, useState, type FC } from "react";
import { Outlet, useBlocker, useNavigate, useParams } from "react-router";
import { v4 as uuid } from "uuid";
import { appPaths } from "../app/appPaths.js";
import { useCollapseNavWhileMounted } from "../app/navCollapseContext.js";
import { MastraChatProvider } from "../features/agents/mastra/mastraChatContext.js";
import { useMastraChat } from "../features/agents/mastra/useMastraChat.js";
import type { PlaybookConfigurationAssistantProps } from "../features/agents/playbookConfigurationAssistant/playbookConfigurationAssistant.js";
import type { ViewedPlaybookNode } from "../features/agents/playbookConfigurationAssistant/playbookDraftSuggestionList.js";
import {
  NotificationType,
  showNotification,
} from "../features/notificationsOverlay/notificationsSlice.js";
import { PlaybookVersionHistoryButton } from "../features/versionHistory/playbookVersionHistoryButton.js";
import { useIsFeatureFlagEnabled } from "../helpers/featureFlags/hooks.js";
import { useAppDispatch } from "../hooks/hook.js";
import { useAutosaveDraft } from "../hooks/useAutosaveDraft.js";
import { usePlaybookEditor } from "../hooks/usePlaybookEditor.js";
import { usePlaybookSaveStatus } from "../hooks/usePlaybookSaveStatus.js";
import {
  isOutcomeSampleToolResultEntry,
  useCollectSampleToolResultIdsOnTurnComplete,
  useSampleToolResults,
} from "../hooks/useSampleToolResults.js";
import type {
  EditorContainerRouteProps,
  PlaybookEditorOutletContext,
} from "./playbookEditorOutletContext.js";
import { PlaybookEditorRouteTabs } from "./playbookEditorRouteTabs.js";
import type { PlaybookEditorSelectionId } from "./playbookEditorUrlState.js";
import { PlaybookSaveStatusIndicator } from "./PlaybookSaveStatusIndicator.js";
import { PlaybookStatusPill } from "./PlaybookStatusPill.js";
import type { AssessmentTreeItemKey } from "./recordingAssessmentEditor/assessmentTreeHelpers.js";

export const PlaybookEditorPage: FC = () => {
  useCollapseNavWhileMounted();

  const navigate = useNavigate();
  const { playbookId: playbookIdParam } = useParams();
  if (!playbookIdParam) {
    throw new Error("Missing playbookId URL param");
  }

  // The shared route shell treats `new` as creation and every other param as a
  // branded playbook id; its child routes choose Edit, Feedback, or Settings.
  const isNewPlaybook = playbookIdParam === "new";
  const playbookId = isNewPlaybook ? null : asPlaybookId(playbookIdParam);

  // Publish and discard clear the state `navigationBlocker` reads and then
  // navigate in the same tick. `useBlocker` re-registers its predicate in an
  // effect, so the router would still run the pre-clear closure and prompt on
  // the way out — mark the editor's own exits as deliberate instead.
  const isClosingRef = useRef(false);

  // Memoized so handlePublish/handleDiscard's useCallback deps stay stable.
  const onClose = useCallback(() => {
    isClosingRef.current = true;
    navigate(appPaths.settingsPlaybooks.build());
  }, [navigate]);

  const isKnowledgeBaseEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.KnowledgeBase,
  );
  const isFeedbackReviewEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.FeedbackReviewTab,
  );
  const canReviewFeedback = !isNewPlaybook && isFeedbackReviewEnabled;

  const {
    data,
    initialData,
    liveData,
    feedback,
    isFeedbackLoading,
    isFeedbackError,
    isSaving,
    hasUnsavedChanges,
    hasUnpublishedChanges,
    publishBlockedReason,
    lastSavedAt,
    onChange,
    onDiscardChanges,
    onPublishChanges,
    onPublishPartialChanges,
    onSaveDraft,
  } = usePlaybookEditor(playbookId);
  const dispatch = useAppDispatch();

  // Track which action is in progress (for button loading states)
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  // Every publish builds on the live revision, so one at a time.
  // NOTE: this only serializes publishes from this tab. Two devices publishing
  // (or saving drafts) at once are last-writer-wins with no conflict detection
  // — brittle, but accepted for now.
  const [publishingTarget, setPublishingTarget] =
    useState<PlaybookPublishTarget | null>(null);
  const isActionInProgress =
    isPublishing || isDiscarding || publishingTarget !== null;

  // Route-specific query state is authoritative while its route is active.
  // These values only bridge a tab transition after the source route drops its
  // query params (e.g. Feedback -> Settings -> Edit).
  const [lastStableSelectionId, setLastStableSelectionId] =
    useState<PlaybookEditorSelectionId | null>(null);
  const [reviewStatus, setReviewStatus] =
    useState<RecordingAssessmentFeedbackStatus>("open");

  // High-level playbook-editor variable that accumulates sample-tool
  // results as the assistant runs them. Populated by detecting agent
  // turn completion and pushing the captured `sample_tool_result_id`s
  // into `addIdsToFetch`. The criteria settings pane reads `results`
  // to render the agent's suggestion alongside the selected criterion.
  const {
    results: sampleToolResults,
    addIdsToFetch,
    removeResultForNode: discardSampleToolResultForNode,
    clearResults: clearSampleToolResults,
  } = useSampleToolResults(playbookId);

  // Imperative handle to inject a static assistant message (the "Optimize"
  // clarifying prompt) into the assistant thread without triggering a run.
  const appendAssistantMessageRef = useRef<
    ((message: UIMessage) => void) | null
  >(null);

  // Set up autosave for existing playbooks
  const { flushPendingSave } = useAutosaveDraft({
    hasChanges: hasUnsavedChanges,
    canAutosave: playbookId !== null && !isSaving,
    onSave: onSaveDraft,
  });

  const chat = useMastraChat({
    agentId: PLAYBOOK_CONFIGURATION_AGENT_ID,
    appendAssistantMessageRef,
    onSend: async () => {
      // The agent reads the playbook draft server-side, so pending editor
      // changes (autosave is debounced) must be persisted before the request —
      // otherwise a just-added section/criterion is invisible to the agent. If
      // the save fails after retries, still send: the agent then sees the last
      // persisted draft, which beats blocking the chat on a save error.
      await flushPendingSave();
      return {
        requestContext: {
          playbookId,
          // Tell the agent which un-applied new-criterion suggestions the user
          // currently sees so it can refine one in place instead of minting a
          // duplicate. Only new-criterion proposals are sent — existing-criterion
          // edits already dedup via their stable node id, so surfacing them would
          // just cost a wasted server fetch.
          visibleSampleToolResultIds: Object.values(sampleToolResults)
            .filter((entry) => entry.kind === "new_criterion")
            .map((entry) => entry.id),
        },
      };
    },
  });
  const isOptimizingSectionOrCriterion = chat.isRunning;

  useCollectSampleToolResultIdsOnTurnComplete({
    messages: chat.messages,
    isRunning: chat.isRunning,
    addIdsToFetch,
  });

  // Bumped each time the assistant requests a navigation to a playbook node
  // (criterion or section). The nonce makes re-clicking the same row
  // re-trigger downstream selection even when the id is unchanged.
  const [criterionSelectionRequest, setCriterionSelectionRequest] = useState<{
    id: PlaybookNodeId;
    kind:
      | "existing_criterion"
      | "new_criterion"
      | "existing_section"
      | "new_section"
      | "outcome";
    nonce: number;
  } | null>(null);
  const handleSelectDraftSuggestion = useCallback(
    (
      id: PlaybookNodeId,
      kind:
        | "existing_criterion"
        | "new_criterion"
        | "existing_section"
        | "new_section"
        | "outcome",
    ) => {
      setCriterionSelectionRequest((prev) => ({
        id,
        kind,
        nonce: (prev?.nonce ?? 0) + 1,
      }));
    },
    [],
  );

  // The node currently selected in the editor, normalized into the suggestion
  // id-space so the assistant's suggestion list can flag the "Viewing" row.
  const [viewedNode, setViewedNode] = useState<ViewedPlaybookNode | null>(null);
  const handleCurrentSelectionChange = useCallback(
    (key: AssessmentTreeItemKey | undefined) => {
      setViewedNode(toViewedNode(key));
    },
    [],
  );

  // Compute save status for display
  const saveStatus = usePlaybookSaveStatus({
    hasUnpublishedChanges,
    isSaving,
    lastSavedAt,
  });

  // Handle publish
  const handlePublish = useCallback(async () => {
    setIsPublishing(true);
    try {
      await onPublishChanges();
      // Publish deletes the playbook's sample-tool results server-side; drop
      // the matching client cache so ghosts/suggestions don't linger.
      clearSampleToolResults();
      onClose();
    } finally {
      setIsPublishing(false);
    }
  }, [onPublishChanges, clearSampleToolResults, onClose]);

  // Unlike a whole publish, the editor stays open: the rest of the draft is
  // still pending. The API accepts several targets; today's inline actions
  // intentionally send one at a time.
  const handlePublishPartialTarget = useCallback(
    async (target: PlaybookPublishTarget) => {
      setPublishingTarget(target);
      try {
        // Flush first so the stored draft contains what's being published —
        // otherwise a failed autosave could leave the draft claiming the
        // node's old state as pending, and a later whole publish would revert
        // what was just published.
        const isDraftSaved = await flushPendingSave();
        if (!isDraftSaved) {
          throw new Error("Draft could not be saved before publishing");
        }
        await onPublishPartialChanges([target]);
        if (target.type === "node") {
          // The server dropped this node's saved sample-tool result too.
          discardSampleToolResultForNode(target.node_id);
        } else if (target.type === "outcome") {
          // Mirror the server's cleanup of outcome sample-tool results locally,
          // while preserving unrelated criterion and section results.
          Object.values(sampleToolResults)
            .filter(isOutcomeSampleToolResultEntry)
            .forEach((result) => {
              discardSampleToolResultForNode(result.playbook_node_id);
            });
        }
      } catch (error) {
        console.error("Failed to publish playbook changes:", error);
        showNotification(dispatch, {
          id: `playbook-publish-changes-error-${new Date().getTime().toString()}`,
          type: NotificationType.FAILURE,
          primaryMessage: "Couldn't publish changes",
          secondaryMessage: "Refresh the page and try again.",
        });
      } finally {
        setPublishingTarget(null);
      }
    },
    [
      flushPendingSave,
      onPublishPartialChanges,
      discardSampleToolResultForNode,
      sampleToolResults,
      dispatch,
    ],
  );

  const handlePublishNode = useCallback(
    (nodeId: string) =>
      handlePublishPartialTarget({ type: "node", node_id: nodeId }),
    [handlePublishPartialTarget],
  );

  const handlePublishOutcome = useCallback(
    () => handlePublishPartialTarget({ type: "outcome" }),
    [handlePublishPartialTarget],
  );

  const handlePublishSummaryTemplate = useCallback(
    () => handlePublishPartialTarget({ type: "summary_template" }),
    [handlePublishPartialTarget],
  );

  // Handle discard - revert draft to live (and close only for new playbooks).
  // For new playbooks the editor closes (no live version to revert to); for
  // existing playbooks the user stays on the page showing the reverted data.
  const handleDiscard = useCallback(async () => {
    setIsDiscarding(true);
    try {
      await onDiscardChanges();
      // Discard deletes the playbook's sample-tool results server-side; drop
      // the matching client cache so ghosts/suggestions don't linger.
      clearSampleToolResults();
      if (isNewPlaybook) {
        onClose();
      }
    } finally {
      setIsDiscarding(false);
    }
  }, [onDiscardChanges, clearSampleToolResults, isNewPlaybook, onClose]);

  // Discard is destructive — it reverts the draft and server-side deletes
  // every saved AI sample-tool result for the playbook. Always confirm; copy
  // mentions the AI suggestions only when there are any to lose.
  const hasSampleToolSuggestions = Object.keys(sampleToolResults).length > 0;

  // A new playbook has nothing to fall back on — no draft is created until
  // publish, so leaving discards the work.
  const hasUnpublishedNewPlaybook = isNewPlaybook && hasUnsavedChanges;

  // Block every pathname change (including editor tab routes) while this page
  // holds work that can't be recovered: an unpublished new playbook, or AI
  // suggestions, which live only in memory and can't be rebuilt without
  // re-running the agent. Query-only selection changes remain on this pathname.
  const navigationBlocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !isClosingRef.current &&
      (hasSampleToolSuggestions || hasUnpublishedNewPlaybook) &&
      currentLocation.pathname !== nextLocation.pathname,
  );
  const {
    renderConfirmationModal: renderDiscardConfirmationModal,
    onConfirmThenClose: confirmThenDiscard,
  } = useConfirmationModal({
    needsConfirmation: true,
    onClose: () => {
      void handleDiscard();
    },
  });

  // Handler for applying AI assistant operations
  const handleApplyOperation = async (operation: PlaybookOperation) => {
    if (!data) {
      throw new Error("Cannot apply operation when playbook not loaded");
    }

    const newTree = applyPlaybookOperation(data.tree, operation);

    if (newTree) {
      onChange({ tree: newTree });
    } else {
      throw new Error("Failed to apply playbook operation");
    }
  };

  const handleOptimizeCriteria = useCallback(
    (criteria: PlaybookCriterionNode) => {
      appendAssistantMessageRef.current?.(
        buildOptimizeCriteriaAssistantMessage(criteria),
      );
    },
    [],
  );

  const handleTestCriteria = useCallback(
    (criteria: PlaybookCriterionNode) => {
      chat.sendMessage(buildTestCriteriaUserMessage(criteria));
    },
    [chat],
  );

  const handleOptimizeSection = useCallback((section: PlaybookSectionNode) => {
    appendAssistantMessageRef.current?.(
      buildOptimizeSectionAssistantMessage(section),
    );
  }, []);

  const handleTestSection = useCallback(
    (section: PlaybookSectionNode) => {
      chat.sendMessage(buildTestSectionUserMessage(section));
    },
    [chat],
  );

  const handleOptimizeOutcome = useCallback((outcome: OutcomeDefinition) => {
    appendAssistantMessageRef.current?.(
      buildOptimizeOutcomeAssistantMessage(outcome),
    );
  }, []);

  const handleTestOutcome = useCallback(
    (outcome: OutcomeDefinition) => {
      chat.sendMessage(buildTestOutcomeUserMessage(outcome));
    },
    [chat],
  );

  const editorProps = {
    data,
    initialOutcome: initialData?.outcome ?? null,
    liveOutcome: liveData?.outcome,
    liveSummaryTemplateBody: liveData?.summaryTemplateBody,
    liveTree: liveData?.tree,
    feedback,
    onChange,
    onOptimizeCriterion: playbookId ? handleOptimizeCriteria : undefined,
    onTestCriterion: playbookId ? handleTestCriteria : undefined,
    onOptimizeSection: playbookId ? handleOptimizeSection : undefined,
    onTestSection: playbookId ? handleTestSection : undefined,
    onOptimizeOutcome: playbookId ? handleOptimizeOutcome : undefined,
    onTestOutcome: playbookId ? handleTestOutcome : undefined,
    onPublishNode: playbookId ? handlePublishNode : undefined,
    onPublishOutcome: playbookId ? handlePublishOutcome : undefined,
    onPublishSummaryTemplate: playbookId
      ? handlePublishSummaryTemplate
      : undefined,
    publishingNodeId:
      publishingTarget?.type === "node" ? publishingTarget.node_id : undefined,
    isPublishingOutcome: publishingTarget?.type === "outcome",
    isPublishingSummaryTemplate: publishingTarget?.type === "summary_template",
    isPublishBlocked: isActionInProgress,
    isOptimizingSectionOrCriterion,
    sampleToolResults,
    onDiscardSampleToolResultForNode: discardSampleToolResultForNode,
    playbookId,
    criterionSelectionRequest,
  } satisfies EditorContainerRouteProps;

  const assistantProps = playbookId
    ? ({
        playbookTree: data?.tree,
        outcomeDefinition: data?.outcome,
        isKnowledgeBaseEnabled: isKnowledgeBaseEnabled ?? false,
        onApplyOperation: handleApplyOperation,
        sampleToolResults,
        onSelectDraftSuggestion: handleSelectDraftSuggestion,
        viewedNode,
      } satisfies PlaybookConfigurationAssistantProps)
    : null;

  const outletContext = {
    playbookIdParam,
    playbookId,
    canReviewFeedback,
    data,
    liveTree: liveData?.tree,
    feedback,
    isFeedbackLoading,
    isFeedbackError,
    showsKnowledgeBase: isKnowledgeBaseEnabled ?? false,
    onChange,
    editorProps,
    assistantProps,
    onViewedSelectionChange: handleCurrentSelectionChange,
    onStableSelectionChange: setLastStableSelectionId,
    reviewStatus,
    onReviewStatusChange: setReviewStatus,
  } satisfies PlaybookEditorOutletContext;

  return (
    <MastraChatProvider chat={chat}>
      {renderDiscardConfirmationModal({
        title: "Discard changes?",
        message: hasSampleToolSuggestions
          ? "You'll lose any unpublished draft changes and the saved AI suggestions for this playbook."
          : "You'll lose any unpublished draft changes for this playbook.",
        confirmText: "Discard",
      })}
      <ConfirmationModal
        isOpen={navigationBlocker.state === "blocked"}
        onOpenChange={(open) => {
          if (!open && navigationBlocker.state === "blocked") {
            navigationBlocker.reset();
          }
        }}
        title="Leave the playbook editor?"
        confirmText="Leave"
        onConfirm={() => {
          clearSampleToolResults();
          if (navigationBlocker.state === "blocked") {
            navigationBlocker.proceed();
          }
        }}
      >
        {hasUnpublishedNewPlaybook
          ? hasSampleToolSuggestions
            ? "This playbook hasn't been published yet, and you have unsaved AI suggestions for it. Both will be lost if you navigate away."
            : "This playbook hasn't been published yet. It'll be lost if you navigate away."
          : "You have unsaved AI suggestions for this playbook. They'll be lost if you navigate away."}
      </ConfirmationModal>
      <div className="flex h-full flex-col">
        <PageHeader
          title={data?.name}
          isLoading={!data}
          tabs={
            <PlaybookEditorRouteTabs
              playbookIdParam={playbookIdParam}
              canReviewFeedback={canReviewFeedback}
              liveTree={liveData?.tree}
              lastStableSelectionId={lastStableSelectionId}
              reviewStatus={reviewStatus}
            />
          }
          actions={
            <>
              {/* Status indicators - only show for existing playbooks. "Saving
                changes" text sits to the left of the Draft pill. */}
              {playbookId && (
                <>
                  <PlaybookSaveStatusIndicator status={saveStatus} />
                  <PlaybookStatusPill isLive={!hasUnpublishedChanges} />
                </>
              )}
              {hasUnpublishedChanges && (
                <Button
                  variant={ButtonVariant.Secondary}
                  onClick={confirmThenDiscard}
                  isDisabled={isActionInProgress}
                  isLoading={isDiscarding}
                  size={Size.MEDIUM}
                >
                  Discard
                </Button>
              )}
              <TooltipTrigger isDisabled={publishBlockedReason === null}>
                <TooltipHoverTarget
                  shape="rounded"
                  isDisabled={publishBlockedReason === null}
                >
                  <Button
                    variant={ButtonVariant.Primary}
                    onClick={handlePublish}
                    isDisabled={
                      !hasUnpublishedChanges ||
                      !data ||
                      isActionInProgress ||
                      publishBlockedReason !== null
                    }
                    isLoading={isPublishing}
                    size={Size.MEDIUM}
                  >
                    Publish
                  </Button>
                </TooltipHoverTarget>
                <SimpleTooltip>{publishBlockedReason}</SimpleTooltip>
              </TooltipTrigger>
              {playbookId ? (
                <PlaybookVersionHistoryButton
                  playbookId={playbookId}
                  isDisabled={isActionInProgress || hasUnsavedChanges}
                  onRollbackComplete={clearSampleToolResults}
                />
              ) : null}
            </>
          }
        />

        <Outlet context={outletContext} />
      </div>
    </MastraChatProvider>
  );
};

/**
 * Map the editor's selected tree key into the suggestion id-space so the
 * assistant's suggestion list can flag the row that's currently being viewed.
 * Keys without a corresponding suggestion row (knowledge base, summary
 * template, the "new node" placeholders) map to `null`. Mirror of the
 * suggestion-kind → selection mapping in `handleSelectDraftSuggestion`.
 */
function toViewedNode(
  key: AssessmentTreeItemKey | undefined,
): ViewedPlaybookNode | null {
  if (!key) return null;
  switch (key.type) {
    case "criteria":
      return { kind: "existing_criterion", id: key.id };
    case "section":
      return { kind: "existing_section", id: key.id };
    case "ghost-criterion":
      return { kind: "new_criterion", id: key.id };
    case "ghost-section":
      return { kind: "new_section", id: key.id };
    case "outcome":
      // A playbook has a single outcome pane; the id is matched by kind only.
      return { kind: "outcome", id: key.id };
    default:
      return null;
  }
}

function buildOptimizeCriteriaAssistantMessage(
  criteria: PlaybookCriterionNode,
): UIMessage {
  return {
    id: `optimize-criterion-${uuid()}`,
    role: "assistant",
    parts: [
      {
        type: "text",
        text: `I can help you optimize this criterion (criterion id: ${criteria.id}) based on what you want to improve.
What would you like to optimize about this criterion?
1. Make grading more consistent
2. Improve outcome correlation
3. Reduce score clustering
4. Clarify when this criterion should be marked N/A
5. Run a broad diagnostic and recommend the best improvement
6. Something else

If you specify which category of change you want to make, and give me some specifics about what feels wrong or unreliable, I can draft updates for you.`,
      },
    ],
  };
}

function buildTestCriteriaUserMessage(criteria: PlaybookCriterionNode) {
  return `Test the "${criteria.name}" criterion as it stands today — do not propose a rewrite.

Look up this criterion's ID in the current Playbook configuration, then use the sample criteria score distribution and outcome correlation tool with the criterion's current fields. Keep the candidate draft identical to the saved criterion — same name, grading type, grading context, and description — so the tool reports how the criterion behaves right now, not a proposed change.
Common things to surface from the test (not exhaustive):
- The score distribution across recent gradings — which buckets dominate and which are rare.
- The outcome correlation against the team's outcome definition.
- Patterns worth flagging, e.g. the criterion rarely fires, the distribution is skewed, or correlation with the outcome is weak.

Share the findings. Only draft a replacement criterion if I explicitly ask for one in a follow-up.`;
}

function buildOptimizeSectionAssistantMessage(
  section: PlaybookSectionNode,
): UIMessage {
  return {
    id: `optimize-section-${uuid()}`,
    role: "assistant",
    parts: [
      {
        type: "text",
        text: `I can help you optimize this section (section id: ${section.id}) based on what you want to improve.
What would you like to optimize about this section?
1. Clarify when this section applies
2. Reduce over-application or under-application
3. Improve the criteria inside this section
4. Restructure or split this section
5. Run a broad diagnostic and recommend the best improvement
6. Something else

If you specify which category of change you want to make, and give me some specifics about what feels wrong or unreliable, I can draft updates for you.`,
      },
    ],
  };
}

function buildTestSectionUserMessage(section: PlaybookSectionNode) {
  return `Test the "${section.name}" section as it stands today — do not propose a rewrite.

Look up this section's ID in the current Playbook configuration, then use the sample section applicability distribution tool with the section's current fields. Keep the candidate draft identical to the saved section — same name and description — so the tool reports how the section behaves right now, not a proposed change.
Common things to surface from the test (not exhaustive):
- The applicable vs not-applicable distribution across recent gradings.
- Whether the applicability rubric is clear, consistent, observable from the transcript, and non-overlapping with other sections.
- Patterns worth flagging, e.g. the section is almost always applicable (or almost never), or its boundaries are inconsistent.

Share the findings. Only draft a replacement section if I explicitly ask for one in a follow-up.`;
}

function buildOptimizeOutcomeAssistantMessage(
  outcome: OutcomeDefinition,
): UIMessage {
  return {
    id: `optimize-outcome-${uuid()}`,
    role: "assistant",
    parts: [
      {
        type: "text",
        text: `I can help you optimize this outcome (the "${outcome.name}" outcome) based on what you want to improve.
What would you like to optimize about this outcome?
1. Improve classification consistency
2. Reduce ambiguous edge cases between outcome labels
3. Check whether the outcome definition matches real calls
4. Run a broad diagnostic and recommend the best improvement
5. Something else

If you specify which category of change you want to make, and give me some specifics about what feels wrong or unreliable, I can draft updates for you.`,
      },
    ],
  };
}

function buildTestOutcomeUserMessage(outcome: OutcomeDefinition) {
  return `Test the playbook's "${outcome.name}" outcome rubric as it stands today — do not propose a rewrite.

Use the sample outcome distribution tool with the outcome's current grading context. Keep the candidate draft's context identical to the saved outcome so the tool reports how the outcome is classified right now, not a proposed change.
Common things to surface from the test (not exhaustive):
- The distribution of calls across the outcome's result values (e.g. won / lost / N/A).
- Whether the rubric is clear, consistent, and observable from the transcript alone.
- Patterns worth flagging, e.g. one result value dominates, or N/A is assigned far more or less than expected.

Share the findings. Only draft a replacement rubric if I explicitly ask for one in a follow-up.`;
}
